import { Component, OnInit } from '@angular/core';
import { IAttachment } from '@common/interfaces/attachment';
import { AppService } from '@ep-om/app.service';
import { requiredAttachemntValidator } from '@ep-om/project/components/validators/angular-forms/custom-validators';
import { AttachmentService } from '@ep-om/project/state/attachment/attachment.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldType } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { allowedExt as picturesExt, PhotoVM } from '../formly-foto-upload/formly-foto-upload.component';

const knownFileFormats = ['pdf', 'txt']; 
const colorMap = new Map<string, string>([
  ['red', '#e32636'],
  ['blue', '#0000ff'],
  ['green', '#029b00']
]);

type FileVM = {
  attachment: IAttachment;
  iconColor: string;
  iconNzType: string;
};

type AttachmentVM = (FileVM | PhotoVM) & {isPicture: boolean};

const SEP = ',';

@Component({
  selector: 'formly-file-upload',
  templateUrl: './formly-file-upload.component.html'
})
@UntilDestroy()
export class FormlyFileUploadComponent extends FieldType implements OnInit {
  vm$: Observable<AttachmentVM[]>;

  constructor(
    private _attachmentService: AttachmentService,
    private appService: AppService,
    private nzns: NzNotificationService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    if (!!this.props?.required) {
      this.formControl.addValidators(requiredAttachemntValidator);
    } else {
      this.formControl.clearValidators();
    }
    this.formControl.updateValueAndValidity();

    this._initVm();
  }

  _initVm(): void {
    this.vm$ = this.formControl.valueChanges.pipe(
      untilDestroyed(this),
      startWith(this.formControl.value),
      distinctUntilChanged(),
      map((value) => {
        const ids = value ? String(value).split(SEP) : [];
        return this._attachmentService.query
          .getByIssueId(this.model.issueId)
          .filter((att) => ids.includes(att.id) && att?.interfaceId === this.model.id);
      }),
      map((attachments) => attachments.filter((att) => !att?.hidden)),
      map((attachments) => attachments.map((attachment => this._getVmByAttachmentType(attachment)))));
  }

  private _getVmByAttachmentType(attachment: IAttachment): AttachmentVM {
    const attachmentExtension: string = this._attachmentService.getFileExtension(attachment.fileName);
    if (picturesExt.includes(attachmentExtension)) {
      return {
        attachment,
        base64Thumbnail: this._attachmentService.bufferToBase64(attachment.thumbnail),
        isPicture: true
      };
    }
    const { iconExt, iconColor } = this._attachmentService.getFileIconData(attachmentExtension);
    return {
      attachment: attachment,
      iconColor: colorMap.get(iconColor) || '#b8b4aa',
      iconNzType: `file-${iconExt}`,
      isPicture: false
    };
  }

  handleUpload = async (item: any) => {
    const fileExtension: string = this._attachmentService.getFileExtension(item.file.name);    

    // is file safe
    const isSafe = await this._attachmentService.isFileSafe(item?.file);
    if (!isSafe) {
      this.nzns.error(this.translateService.instant('ERROR'), this.translateService.instant('ATTACHMENTS.UNSUPPORTED_FORMAT'));
      return;
    }
    
    // file size checks
    const fileSize = item.file.size;
    const fileSizeLimit = this.appService.SETTINGS.attachmentOptions?.limitSize;
    const isAboveSizeLimit = fileSize / 1024 / 1024 > fileSizeLimit;
    if (isAboveSizeLimit) {
      this.nzns.error(this.translateService.instant('ERROR'), this.translateService.instant('ATTACHMENTS.ABOVE_MAX_LIMIT', { fileSizeLimit }));
      return;
    }

    // files already exhists
    const alreadyExists = this._attachmentService.doesAttachmentAlreadyExist(item.file.name, this.model?.issueId)
    if (alreadyExists) {
      this.nzns.error(this.translateService.instant('ERROR'), this.translateService.instant('ATTACHMENTS.ALREADY_EXISTING'));
      return;
    }

    // is file allowed
    if (picturesExt.includes(fileExtension)) {
      await this._attachmentService.createPhotoAttachment(item.file, this.model?.issueId, this.model.id)
      .then((res) => this._addFormAttachment(res.createdAttachment.id))
      .catch(err => console.error(err)); 
    } else {
      await this._attachmentService
      .createAttachment(item.file, this.model?.issueId, this.model.id)
      .then((res) => this._addFormAttachment(res.createdAttachment.id))
      .catch((err) => console.error(err));
    }   
  };

  _deleteAttachment(attId: string): void {
    if (this.formControl.disabled) {
      this.nzns.error(this.translateService.instant('TOAST.ERROR'), this.translateService.instant('ATTACHMENTS.FORBIDDEN_ACTION'));
      return;
    }
    this._attachmentService.trash(false, attId, (response) => {
      if (response.status === 'success') {
        this._removeFormAttachment(attId);
        this.nzns.success(this.translateService.instant('TOAST.SUCCESS'), this.translateService.instant('ATTACHMENTS.DELETE_SUCCESS'));
      } else {
        this.nzns.error(this.translateService.instant('TOAST.ERROR'), this.translateService.instant('ATTACHMENTS.DELETE_FAILED'));
      }
    });
  }

  async _showAttachments(vm: AttachmentVM): Promise<void> {
    const fileExtension = this._attachmentService.getFileExtension(vm.attachment.fileName);
    if (vm.isPicture && vm.attachment?.thumbnail) {
      this._openPhotoModal(vm.attachment);
      return;
    }
    this._downloadAttachment(vm.attachment);
  }

  private async _openPhotoModal(attachment: IAttachment) {
    this._attachmentService.openPhotoModal(attachment, false);
  }

  _downloadAttachment(attachment: IAttachment): void {
    this._attachmentService.downloadAttachment(attachment);
  }

  private _removeFormAttachment(attachmentId: string): void {
    const oldValue = this.formControl?.value ? String(this.formControl.value).split(SEP) : [];
    const filtered = oldValue.filter((val) => val !== attachmentId).join(SEP);
    this.formControl.patchValue(filtered);
    this.formControl.markAsTouched();
  }

  private _addFormAttachment(attachmentId: string): void {
    const oldValue = this.formControl?.value ? String(this.formControl.value).split(SEP) : [];
    if (!oldValue.includes(attachmentId)) {
      oldValue.push(attachmentId);
    }
    this.formControl.patchValue(oldValue.join(SEP));
    this.formControl.markAsTouched();
  }
}
